import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Search, Eye, ChevronLeft, ChevronRight, ArrowUpDown, CreditCard, FileText, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const CustomersListPage = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [sellerFilter, setSellerFilter] = useState('');
  const [sellersList, setSellersList] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = { search, page, limit: 10 };
      if (statusFilter) params.status = statusFilter;
      if (sortBy) params.sort_by = sortBy;
      if (paymentMethodFilter) params.payment_method = paymentMethodFilter;
      if (sellerFilter) params.seller_code = sellerFilter;
      
      const data = await api.getCustomers(token, params);
      setCustomers(data.customers);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [page, statusFilter, sortBy, paymentMethodFilter, sellerFilter]);

  useEffect(() => {
    // Load sellers for the dropdown filter
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/admin/sellers?status=active`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setSellersList(d.sellers || []))
      .catch(() => setSellersList([]));
  }, [token]);

  const handleExportCsv = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams();
    if (paymentMethodFilter) params.set('payment_method', paymentMethodFilter);
    if (sellerFilter) params.set('seller_code', sellerFilter);
    if (search) params.set('search', search);
    try {
      const r = await fetch(`${API_URL}/api/admin/customers/export.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Export échoué');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-export.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, variant: 'destructive' });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers();
  };

  const getStatusBadge = (customer) => {
    const subs = customer.subscriptions || [];
    const isInvoice = customer.payment_method === 'invoice';

    // Invoice-specific statuses
    if (isInvoice) {
      const hasPaid = subs.some((s) => s.status === 'active');
      if (hasPaid) {
        return (
          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full" data-testid="status-active">
            Actif
          </span>
        );
      }
      const hasInvoiceSent = subs.some((s) => s.invoice_status === 'invoice_sent');
      if (hasInvoiceSent) {
        return (
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full" data-testid="status-pending-payment">
            Facture envoyée
          </span>
        );
      }
      return (
        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full" data-testid="status-pending-invoice">
          En attente
        </span>
      );
    }

    const activeCount = customer.active_subscriptions || 0;
    if (activeCount > 0) {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Actif
        </span>
      );
    }
    
    // Check if has paused subscriptions
    const hasPaused = subs.some(s => s.status === 'paused');
    if (hasPaused) {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
          En pause
        </span>
      );
    }
    
    // Check if has ended subscriptions
    const hasEnded = subs.some(s => 
      ['expired', 'cancelled', 'ended', 'completed'].includes(s.status)
    );
    
    if (hasEnded) {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
          Terminé
        </span>
      );
    }
    
    return (
      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
        Inactif
      </span>
    );
  };

  const getPaymentMethodBadge = (customer) => {
    if (customer.payment_method === 'invoice') {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 rounded-full"
          data-testid="badge-payment-invoice"
        >
          <FileText className="w-3 h-3" /> Facture
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-sky-100 text-sky-800 border border-sky-200 rounded-full"
        data-testid="badge-payment-card"
      >
        <CreditCard className="w-3 h-3" /> Carte
      </span>
    );
  };

  const getPeriodDisplay = (customer) => {
    const latest = customer.latest_subscription;
    if (!latest) return '-';
    
    const current = latest.billing_period_count || 0;
    const total = latest.commitment_months || 0;
    
    if (total === 0) return '-';
    return `${current}/${total}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Abonnés</h1>
        <p className="text-gray-600 mt-1">Gérez vos clients et leurs abonnements</p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, email ou téléphone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Rechercher
              </Button>
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-wrap gap-3">
              <div className="w-48">
                <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val === " " ? "" : val)}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Tous les statuts</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="paused">En pause</SelectItem>
                    <SelectItem value="ended">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-48">
                <Select
                  value={paymentMethodFilter}
                  onValueChange={(val) => { setPaymentMethodFilter(val === " " ? "" : val); setPage(1); }}
                >
                  <SelectTrigger data-testid="filter-payment-method">
                    <SelectValue placeholder="Mode de paiement" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Tous</SelectItem>
                    <SelectItem value="card">Carte</SelectItem>
                    <SelectItem value="invoice">Facture</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-56">
                <Select
                  value={sellerFilter || ' '}
                  onValueChange={(val) => { setSellerFilter(val === ' ' ? '' : val); setPage(1); }}
                >
                  <SelectTrigger data-testid="filter-seller">
                    <SelectValue placeholder="Vendeur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Tous les vendeurs</SelectItem>
                    {sellersList.map((s) => (
                      <SelectItem key={s.id} value={s.seller_code}>
                        {s.seller_code} — {s.first_name} {s.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-48">
                <Select value={sortBy} onValueChange={(val) => setSortBy(val === " " ? "" : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Trier par date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Par défaut</SelectItem>
                    <SelectItem value="oldest">Plus ancien</SelectItem>
                    <SelectItem value="newest">Plus récent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleExportCsv}
                className="ml-auto"
                data-testid="btn-export-csv"
              >
                <Download className="w-4 h-4 mr-1" /> Exporter CSV
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Clients</CardTitle>
          <CardDescription>Total : {total} client{total > 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun client trouvé
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Mode
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Téléphone
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Ville
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Période
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50" data-testid={`customer-row-${customer.id}`}>
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-900">
                            {customer.first_name} {customer.last_name}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {getPaymentMethodBadge(customer)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {customer.email}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {customer.phone}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {customer.city}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          {getPeriodDisplay(customer)}
                        </td>
                        <td className="px-4 py-4">{getStatusBadge(customer)}</td>
                        <td className="px-4 py-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/customers/${encodeURIComponent(customer.id)}`)}
                            className="flex items-center gap-2"
                            data-testid={`view-customer-${customer.id}`}
                          >
                            <Eye size={16} />
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Page {page} sur {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft size={16} />
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      Suivant
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersListPage;
